import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Tab,
  Tabs,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  Business,
  TrendingUp,
  CheckCircle,
  Cancel,
  Star,
  Schedule,
  EventNote,
  Visibility,
  PlayArrow,
  Refresh,
  Close
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { HEADERS } from '../../../API/UrlAndPaths';

const LeadsManagement = () => {
  const currencySymbol = useSelector((state) => state.mainDashBoardSlice?.currencyDetails?.symbol) || '₹';
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '', roomNo: '', buildingNo: '', city: '' });
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [selectedLeadForFollowUp, setSelectedLeadForFollowUp] = useState(null);
  const [pendingFollowUps, setPendingFollowUps] = useState([]);
  const [showPendingFollowUps, setShowPendingFollowUps] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: 'OTHER',
    status: 'NEW',
    priority: 'MEDIUM',
    estimatedValue: 0,
    probability: 50,
    expectedCloseDate: '',
    assignedTo: '',
    notes: '',
    // Follow-up settings
    followUpEnabled: true,
    followUpFrequency: 'WEEKLY',
    followUpCustomDays: 7,
    followUpType: 'CALL',
    // Notification channels for assigned employee
    notifyChannels: ['EMAIL'] // Default to email, options: EMAIL, WHATSAPP, KOOPPAI
  });

  useEffect(() => {
    fetchLeads();
    fetchEmployees();
    fetchCustomers();
    fetchPendingFollowUps();
  }, []);

  // Fetch pending follow-ups
  const fetchPendingFollowUps = async () => {
    try {
      const res = await axios.get('/api/crm-chat/follow-ups/pending', HEADERS);
      if (res.data.success) {
        setPendingFollowUps(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching pending follow-ups:', error);
    }
  };

  // Complete a follow-up
  const handleCompleteFollowUp = async (leadId, followUpId) => {
    const { value: formValues } = await Swal.fire({
      title: 'Complete Follow-up',
      html:
        '<select id="swal-outcome" class="swal2-select" style="width:100%;margin-bottom:10px">' +
          '<option value="INTERESTED">Interested</option>' +
          '<option value="NOT_INTERESTED">Not Interested</option>' +
          '<option value="CALLBACK">Callback Requested</option>' +
          '<option value="NO_ANSWER">No Answer</option>' +
          '<option value="MEETING_SCHEDULED">Meeting Scheduled</option>' +
          '<option value="CONVERTED">Converted</option>' +
        '</select>' +
        '<textarea id="swal-notes" class="swal2-textarea" placeholder="Notes (optional)" style="width:100%"></textarea>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Complete',
      preConfirm: () => {
        return {
          outcome: document.getElementById('swal-outcome').value,
          notes: document.getElementById('swal-notes').value
        };
      }
    });

    if (formValues) {
      try {
        const res = await axios.post(`/api/crm-chat/leads/${leadId}/follow-ups/${followUpId}/complete`, formValues, HEADERS);
        if (res.data.success) {
          Swal.fire('Success', 'Follow-up completed!', 'success');
          fetchLeads();
          fetchPendingFollowUps();
        }
      } catch (error) {
        Swal.fire('Error', 'Failed to complete follow-up', 'error');
      }
    }
  };

  // Reschedule a follow-up
  const handleRescheduleFollowUp = async (leadId, followUpId) => {
    const { value: formValues } = await Swal.fire({
      title: 'Reschedule Follow-up',
      html:
        '<input type="date" id="swal-date" class="swal2-input" style="width:100%">' +
        '<textarea id="swal-reason" class="swal2-textarea" placeholder="Reason for rescheduling" style="width:100%"></textarea>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Reschedule',
      preConfirm: () => {
        const newDate = document.getElementById('swal-date').value;
        if (!newDate) {
          Swal.showValidationMessage('Please select a new date');
          return false;
        }
        return {
          newDate,
          reason: document.getElementById('swal-reason').value
        };
      }
    });

    if (formValues) {
      try {
        const res = await axios.post(`/api/crm-chat/leads/${leadId}/follow-ups/${followUpId}/reschedule`, formValues, HEADERS);
        if (res.data.success) {
          Swal.fire('Success', 'Follow-up rescheduled!', 'success');
          fetchLeads();
          fetchPendingFollowUps();
        }
      } catch (error) {
        Swal.fire('Error', 'Failed to reschedule follow-up', 'error');
      }
    }
  };

  // View follow-ups for a lead
  const handleViewFollowUps = async (lead) => {
    setSelectedLeadForFollowUp(lead);
    setFollowUpDialogOpen(true);
  };

  // Add manual follow-up
  const handleAddFollowUp = async (leadId) => {
    const { value: formValues } = await Swal.fire({
      title: 'Add Follow-up',
      html:
        '<input type="date" id="swal-date" class="swal2-input" style="width:100%">' +
        '<select id="swal-type" class="swal2-select" style="width:100%;margin-top:10px">' +
          '<option value="CALL">Call</option>' +
          '<option value="EMAIL">Email</option>' +
          '<option value="MEETING">Meeting</option>' +
          '<option value="WHATSAPP">WhatsApp</option>' +
          '<option value="VISIT">Visit</option>' +
        '</select>' +
        '<textarea id="swal-notes" class="swal2-textarea" placeholder="Notes (optional)" style="width:100%"></textarea>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Add',
      preConfirm: () => {
        const scheduledDate = document.getElementById('swal-date').value;
        if (!scheduledDate) {
          Swal.showValidationMessage('Please select a date');
          return false;
        }
        return {
          scheduledDate,
          type: document.getElementById('swal-type').value,
          notes: document.getElementById('swal-notes').value
        };
      }
    });

    if (formValues) {
      try {
        const res = await axios.post(`/api/crm-chat/leads/${leadId}/follow-ups`, formValues, HEADERS);
        if (res.data.success) {
          Swal.fire('Success', 'Follow-up added!', 'success');
          fetchLeads();
          fetchPendingFollowUps();
        }
      } catch (error) {
        Swal.fire('Error', 'Failed to add follow-up', 'error');
      }
    }
  };

  const fetchEmployees = async () => {
    try {
      // Match existing app behaviour (see parollAPIs.listAllEmployees)
      const loginMeta = JSON.parse(localStorage.getItem('login_meta') || '{}');
      const branchId = loginMeta?.branchPk || loginMeta?.branchId || localStorage.getItem('branchId') || '';

      const res = await axios.get(`employee/viewAllEmployeesByBranch`, {
        params: { branchId }
      });

      if (res.status === 200 && Array.isArray(res.data)) {
        // API returns plain array; items use staff_name
        const employeeList = res.data.map(emp => ({
          _id: emp._id,
          name: emp.staff_name || emp.name || '',
          empId: emp.empId
        }));
        setEmployees(employeeList);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      // Use the correct endpoint for fetching existing customers
      const res = await axios.post('account/viewCustomerForDropdown', {}, HEADERS);
      if (res.status === 200 && Array.isArray(res.data)) {
        setCustomers(res.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      if (!newCustomer.name || !newCustomer.phone) {
        alert('Name and Phone are required');
        return;
      }
      const res = await axios.post('customer/addCustomer', {
        name: newCustomer.name,
        mobileNo: newCustomer.phone,
        email: newCustomer.email,
        roomNo: newCustomer.roomNo,
        buildingNo: newCustomer.buildingNo,
        city: newCustomer.city,
        status: true,
        isCustomer: true,
        isVendor: false
      }, HEADERS);
      if (res.status === 200) {
        setCustomerDialogOpen(false);
        setNewCustomer({ name: '', phone: '', email: '', roomNo: '', buildingNo: '', city: '' });
        fetchCustomers(); // Refresh customer list
        // Auto-fill the lead form with new customer
        setFormData({
          ...formData,
          name: newCustomer.name,
          phone: newCustomer.phone,
          email: newCustomer.email
        });
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      alert('Failed to create customer');
    }
  };

  useEffect(() => {
    filterLeads();
  }, [leads, tabValue]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/crm-chat/leads', HEADERS);
      if (res.data.success && Array.isArray(res.data.data)) {
        setLeads(res.data.data);
      } else {
        setLeads([]);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-show pending follow-ups popup when new items appear
  useEffect(() => {
    if (pendingFollowUps && pendingFollowUps.length > 0) {
      setShowPendingFollowUps(true);
    }
  }, [pendingFollowUps?.length]);

  const filterLeads = () => {
    if (!Array.isArray(leads)) {
      setFilteredLeads([]);
      return;
    }
    let filtered = leads;
    switch (tabValue) {
      case 0: // All
        break;
      case 1: // New
        filtered = leads.filter(l => l.status === 'NEW');
        break;
      case 2: // Qualified
        filtered = leads.filter(l => ['CONTACTED', 'QUALIFIED'].includes(l.status));
        break;
      case 3: // Proposal
        filtered = leads.filter(l => ['PROPOSAL', 'NEGOTIATION'].includes(l.status));
        break;
      case 4: // Won
        filtered = leads.filter(l => l.status === 'WON');
        break;
      case 5: // Lost
        filtered = leads.filter(l => l.status === 'LOST');
        break;
      default:
        break;
    }
    setFilteredLeads(filtered);
  };

  const handleOpenDialog = (lead = null) => {
    if (lead) {
      setSelectedLead(lead);
      setFormData({
        name: lead.name,
        email: lead.email || '',
        phone: lead.phone,
        company: lead.company || '',
        source: lead.source,
        status: lead.status,
        priority: lead.priority,
        estimatedValue: lead.estimatedValue,
        probability: lead.probability,
        expectedCloseDate: lead.expectedCloseDate ? lead.expectedCloseDate.split('T')[0] : '',
        assignedTo: lead.assignedTo?._id || '',
        notes: lead.notes || '',
        followUpEnabled: lead.followUpEnabled !== false,
        followUpFrequency: lead.followUpFrequency || 'WEEKLY',
        followUpCustomDays: lead.followUpCustomDays || 7,
        followUpType: 'CALL'
      });
    } else {
      setSelectedLead(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        source: 'OTHER',
        status: 'NEW',
        priority: 'MEDIUM',
        estimatedValue: 0,
        probability: 50,
        expectedCloseDate: '',
        assignedTo: '',
        notes: '',
        followUpEnabled: true,
        followUpFrequency: 'WEEKLY',
        followUpCustomDays: 7,
        followUpType: 'CALL'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLead(null);
  };

  const handleSubmit = async () => {
    try {
      // Show sending status if channels selected and employee assigned
      const hasNotifications = formData.assignedTo && formData.notifyChannels?.length > 0;
      
      if (selectedLead) {
        // Update
        const res = await axios.patch(`/api/crm-chat/leads/${selectedLead._id}`, formData, HEADERS);
        if (res.data.success) {
          // Check if assignedTo changed and send notification
          if (formData.assignedTo && formData.assignedTo !== selectedLead.assignedTo?._id) {
            try {
              await axios.post(`/api/crm-chat/leads/${selectedLead._id}/notify-assignment`, {
                channels: formData.notifyChannels
              }, HEADERS);
            } catch (notifyError) {
              console.log('Assignment notification sent or skipped');
            }
          }
          fetchLeads();
          handleCloseDialog();
        }
      } else {
        // Create - show loading with channel info
        if (hasNotifications) {
          Swal.fire({
            title: 'Creating Lead...',
            html: `Sending notifications via: <b>${formData.notifyChannels.join(', ')}</b>`,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
          });
        }
        
        const res = await axios.post('/api/crm-chat/leads', formData, HEADERS);
        if (res.data.success) {
          // Send assignment notification if assigned
          if (formData.assignedTo && res.data.data?._id) {
            try {
              await axios.post(`/api/crm-chat/leads/${res.data.data._id}/notify-assignment`, {
                channels: formData.notifyChannels
              }, HEADERS);
            } catch (notifyError) {
              console.log('Assignment notification sent or skipped');
            }
          }
          fetchLeads();
          fetchPendingFollowUps();
          handleCloseDialog();
          
          // Show success with notification status
          const channelText = formData.notifyChannels?.length > 0 
            ? `\nNotifications sent via: ${formData.notifyChannels.join(', ')}` 
            : '';
          Swal.fire('Success', `Lead created with follow-ups scheduled!${channelText}`, 'success');
        }
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      Swal.fire('Error', 'Failed to save lead', 'error');
    }
  };

  // Send follow-up notifications manually
  const handleSendNotifications = async () => {
    try {
      const res = await axios.post('/api/crm-chat/follow-ups/send-notifications', {}, HEADERS);
      if (res.data.success) {
        Swal.fire('Success', res.data.message, 'success');
        fetchPendingFollowUps();
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to send notifications', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW': return 'info';
      case 'CONTACTED': return 'primary';
      case 'QUALIFIED': return 'success';
      case 'PROPOSAL': return 'warning';
      case 'NEGOTIATION': return 'warning';
      case 'WON': return 'success';
      case 'LOST': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  const getLeadStats = () => {
    const leadsList = Array.isArray(leads) ? leads : [];
    return {
      total: leadsList.length,
      new: leadsList.filter(l => l.status === 'NEW').length,
      qualified: leadsList.filter(l => ['CONTACTED', 'QUALIFIED'].includes(l.status)).length,
      proposal: leadsList.filter(l => ['PROPOSAL', 'NEGOTIATION'].includes(l.status)).length,
      won: leadsList.filter(l => l.status === 'WON').length,
      lost: leadsList.filter(l => l.status === 'LOST').length,
      totalValue: leadsList.reduce((sum, l) => sum + (l.estimatedValue || 0), 0),
      avgScore: leadsList.length > 0 ? 
        leadsList.reduce((sum, l) => sum + (l.aiLeadScore || 0), 0) / leadsList.length : 0
    };
  };

  const stats = getLeadStats();

  return (
    <Box sx={{ p: 3 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Leads</Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>New Leads</Typography>
              <Typography variant="h4" color="primary">{stats.new}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Won</Typography>
              <Typography variant="h4" color="success.main">{stats.won}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Value</Typography>
              <Typography variant="h4">
                {currencySymbol}
                {stats.totalValue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Leads Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {pendingFollowUps.length > 0 && (
            <Tooltip title="Send email notifications for pending follow-ups">
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Email />}
                onClick={handleSendNotifications}
              >
                Send Reminders ({pendingFollowUps.length})
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Lead
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`All (${stats.total})`} />
          <Tab label={`New (${stats.new})`} />
          <Tab label={`Qualified (${stats.qualified})`} />
          <Tab label={`Proposal (${stats.proposal})`} />
          <Tab label={`Won (${stats.won})`} />
          <Tab label={`Lost (${stats.lost})`} />
        </Tabs>
      </Paper>

      {/* Leads Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Probability</TableCell>
              <TableCell>AI Score</TableCell>
              <TableCell>Follow-ups</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : filteredLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography color="textSecondary">No leads found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLeads.map((lead) => (
                <TableRow key={lead._id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{lead.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {lead.source}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone sx={{ fontSize: 14 }} />
                        <Typography variant="body2">{lead.phone}</Typography>
                      </Box>
                      {lead.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email sx={{ fontSize: 14 }} />
                          <Typography variant="body2">{lead.email}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {lead.company && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Business sx={{ fontSize: 14 }} />
                        <Typography variant="body2">{lead.company}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {lead.assignedTo ? (
                      <Chip
                        label={lead.assignedTo.staff_name || lead.assignedTo.name}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lead.status}
                      size="small"
                      color={getStatusColor(lead.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lead.priority}
                      size="small"
                      color={getPriorityColor(lead.priority)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {currencySymbol}
                      {lead.estimatedValue?.toLocaleString() || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={lead.probability || 0}
                        sx={{ flex: 1, height: 8, borderRadius: 1 }}
                      />
                      <Typography variant="caption">{lead.probability}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {lead.aiLeadScore ? (
                      <Tooltip title="AI Lead Score">
                        <Chip
                          icon={<Star />}
                          label={lead.aiLeadScore}
                          size="small"
                          color="warning"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="textSecondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {lead.nextFollowUpDate ? (
                      <Tooltip title={`Next: ${new Date(lead.nextFollowUpDate).toLocaleDateString()}`}>
                        <Chip
                          icon={<Schedule />}
                          label={`${lead.completedFollowUps || 0}/${lead.totalFollowUps || 0}`}
                          size="small"
                          color={new Date(lead.nextFollowUpDate) < new Date() ? 'error' : 'info'}
                          onClick={() => handleViewFollowUps(lead)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit Lead">
                        <IconButton size="small" onClick={() => handleOpenDialog(lead)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Follow-ups">
                        <IconButton size="small" onClick={() => handleViewFollowUps(lead)} color="primary">
                          <EventNote fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Add Follow-up">
                        <IconButton size="small" onClick={() => handleAddFollowUp(lead._id)} color="success">
                          <Add fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedLead ? 'Edit Lead' : 'Add New Lead'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  freeSolo
                  options={customers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.name || '';
                  }}
                  inputValue={formData.name}
                  onInputChange={(event, newInputValue) => {
                    setFormData({ ...formData, name: newInputValue });
                  }}
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue === 'object') {
                      // Selected existing customer - use available fields
                      // Note: Customer dropdown returns: _id, name, mobileNo, roomNo, buildingNo, city
                      setFormData({
                        ...formData,
                        name: newValue.name || '',
                        email: newValue.email || '', // May not be available from dropdown
                        phone: newValue.mobileNo || newValue.phone || ''
                        // company field left unchanged - customer schema doesn't have companyName
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Name / Customer *"
                      placeholder="Search or enter customer name"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    // Ensure stable, unique key per option to avoid duplicate-key warnings
                    <li
                      {...props}
                      key={option._id || option.customerId || option.mobileNo || option.name}
                    >
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        {option.mobileNo && (
                          <Typography variant="caption" color="textSecondary">
                            {option.mobileNo} {option.roomNo && `• Room: ${option.roomNo}`}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Add New Customer">
                  <IconButton 
                    color="primary" 
                    onClick={() => setCustomerDialogOpen(true)}
                    sx={{ mt: 1 }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone *"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  label="Source"
                >
                  <MenuItem value="WEBSITE">Website</MenuItem>
                  <MenuItem value="REFERRAL">Referral</MenuItem>
                  <MenuItem value="SOCIAL_MEDIA">Social Media</MenuItem>
                  <MenuItem value="WALK_IN">Walk-in</MenuItem>
                  <MenuItem value="CALL">Call</MenuItem>
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="NEW">New</MenuItem>
                  <MenuItem value="CONTACTED">Contacted</MenuItem>
                  <MenuItem value="QUALIFIED">Qualified</MenuItem>
                  <MenuItem value="PROPOSAL">Proposal</MenuItem>
                  <MenuItem value="NEGOTIATION">Negotiation</MenuItem>
                  <MenuItem value="WON">Won</MenuItem>
                  <MenuItem value="LOST">Lost</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="URGENT">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Assigned To</InputLabel>
                <Select
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  label="Assigned To"
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp._id} value={emp._id}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Value"
                type="number"
                value={formData.estimatedValue}
                onChange={(e) => setFormData({ ...formData, estimatedValue: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Probability (%)"
                type="number"
                value={formData.probability}
                onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected Close Date"
                type="date"
                value={formData.expectedCloseDate}
                onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Follow-up Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'primary.main' }}>
                <Schedule sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Follow-up Settings
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Enable Follow-ups</InputLabel>
                <Select
                  value={formData.followUpEnabled ? 'yes' : 'no'}
                  onChange={(e) => setFormData({ ...formData, followUpEnabled: e.target.value === 'yes' })}
                  label="Enable Follow-ups"
                >
                  <MenuItem value="yes">Yes - Auto-generate follow-ups</MenuItem>
                  <MenuItem value="no">No - Manual follow-ups only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!formData.followUpEnabled}>
                <InputLabel>Follow-up Frequency</InputLabel>
                <Select
                  value={formData.followUpFrequency}
                  onChange={(e) => setFormData({ ...formData, followUpFrequency: e.target.value })}
                  label="Follow-up Frequency"
                >
                  <MenuItem value="DAILY">Daily</MenuItem>
                  <MenuItem value="WEEKLY">Weekly</MenuItem>
                  <MenuItem value="BIWEEKLY">Bi-weekly (Every 2 weeks)</MenuItem>
                  <MenuItem value="MONTHLY">Monthly</MenuItem>
                  <MenuItem value="CUSTOM">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.followUpFrequency === 'CUSTOM' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Custom Days Interval"
                  type="number"
                  value={formData.followUpCustomDays}
                  onChange={(e) => setFormData({ ...formData, followUpCustomDays: parseInt(e.target.value) || 7 })}
                  inputProps={{ min: 1, max: 90 }}
                  disabled={!formData.followUpEnabled}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!formData.followUpEnabled}>
                <InputLabel>Follow-up Type</InputLabel>
                <Select
                  value={formData.followUpType}
                  onChange={(e) => setFormData({ ...formData, followUpType: e.target.value })}
                  label="Follow-up Type"
                >
                  <MenuItem value="CALL">Phone Call</MenuItem>
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="MEETING">Meeting</MenuItem>
                  <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                  <MenuItem value="VISIT">Visit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.followUpEnabled && formData.expectedCloseDate && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Follow-ups will be auto-generated from tomorrow until {new Date(formData.expectedCloseDate).toLocaleDateString()}
                </Typography>
              </Grid>
            )}
            
            {/* Notification Channels for Assigned Employee */}
            {formData.assignedTo && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={['EMAIL', 'WHATSAPP', 'KOOPPAI']}
                  value={formData.notifyChannels || []}
                  onChange={(e, newValue) => setFormData({ ...formData, notifyChannels: newValue })}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Notify Assigned Employee Via" 
                      placeholder="Select channels..."
                      helperText="Select how to notify the assigned employee about this lead"
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const icons = { EMAIL: <Email fontSize="small" />, WHATSAPP: <Phone fontSize="small" />, KOOPPAI: <Phone fontSize="small" /> };
                    return (
                      <li key={option} {...otherProps}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {icons[option]} {option}
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        size="small"
                        icon={option === 'EMAIL' ? <Email fontSize="small" /> : <Phone fontSize="small" />}
                        color={option === 'EMAIL' ? 'primary' : option === 'WHATSAPP' ? 'success' : 'secondary'}
                      />
                    ))
                  }
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {selectedLead ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name *"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone *"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Room No"
                value={newCustomer.roomNo}
                onChange={(e) => setNewCustomer({ ...newCustomer, roomNo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Building No"
                value={newCustomer.buildingNo}
                onChange={(e) => setNewCustomer({ ...newCustomer, buildingNo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="City"
                value={newCustomer.city}
                onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCustomer}>
            Create Customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Follow-up View Dialog */}
      <Dialog 
        open={followUpDialogOpen} 
        onClose={() => setFollowUpDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Follow-ups for {selectedLeadForFollowUp?.name}</Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedLeadForFollowUp?.completedFollowUps || 0} completed / {selectedLeadForFollowUp?.totalFollowUps || 0} total
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<Add />}
              onClick={() => {
                setFollowUpDialogOpen(false);
                handleAddFollowUp(selectedLeadForFollowUp?._id);
              }}
            >
              Add Follow-up
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLeadForFollowUp?.followUps?.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedLeadForFollowUp.followUps
                  .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
                  .map((followUp, index) => (
                  <TableRow key={followUp._id || index}>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(followUp.scheduledDate).toLocaleDateString()}
                      </Typography>
                      {followUp.completedDate && (
                        <Typography variant="caption" color="success.main">
                          Done: {new Date(followUp.completedDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={followUp.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={followUp.status} 
                        size="small" 
                        color={
                          followUp.status === 'COMPLETED' ? 'success' :
                          followUp.status === 'PENDING' ? (new Date(followUp.scheduledDate) < new Date() ? 'error' : 'info') :
                          followUp.status === 'RESCHEDULED' ? 'warning' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{followUp.outcome || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {followUp.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {followUp.status === 'PENDING' && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Complete">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => {
                                setFollowUpDialogOpen(false);
                                handleCompleteFollowUp(selectedLeadForFollowUp._id, followUp._id);
                              }}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reschedule">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => {
                                setFollowUpDialogOpen(false);
                                handleRescheduleFollowUp(selectedLeadForFollowUp._id, followUp._id);
                              }}
                            >
                              <Refresh fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Schedule sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="textSecondary">No follow-ups scheduled</Typography>
              <Button 
                variant="contained" 
                startIcon={<Add />} 
                sx={{ mt: 2 }}
                onClick={() => {
                  setFollowUpDialogOpen(false);
                  handleAddFollowUp(selectedLeadForFollowUp?._id);
                }}
              >
                Add First Follow-up
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowUpDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Pending Follow-ups Alert */}
      {pendingFollowUps.length > 0 && showPendingFollowUps && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20, 
            p: 2, 
            maxWidth: 350,
            boxShadow: 3,
            zIndex: 1000
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule color="warning" />
              <Typography variant="subtitle2">
                Pending Follow-ups ({pendingFollowUps.length})
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setShowPendingFollowUps(false)}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            {pendingFollowUps.slice(0, 5).map((item, index) => (
              <Box 
                key={index} 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 0.5,
                  borderBottom: index < 4 ? '1px solid #eee' : 'none'
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>{item.leadName}</Typography>
                  <Typography variant="caption" color={item.isOverdue ? 'error' : 'textSecondary'}>
                    {item.isOverdue ? 'Overdue: ' : ''}{new Date(item.scheduledDate).toLocaleDateString()} - {item.type}
                  </Typography>
                </Box>
                <IconButton 
                  size="small" 
                  color="success"
                  onClick={() => handleCompleteFollowUp(item.leadId, item.followUpId)}
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
          {pendingFollowUps.length > 5 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              +{pendingFollowUps.length - 5} more pending
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default LeadsManagement;
